import React, { useEffect, useState, createElement } from 'react';
import { ArrowRightIcon, TrendingUpIcon, MonitorIcon, SmartphoneIcon, SearchIcon, UsersIcon, ShoppingCartIcon, BuildingIcon } from 'lucide-react';
import { FinalCtaSection } from '../components/FinalCtaSection';
interface CaseStudy {
  id: string;
  title: string;
  client: string;
  sector: string;
  category: string;
  description: string;
  result: string;
  icon: React.ElementType;
  color: string;
}
export function PortfolioPage() {
  const [activeFilter, setActiveFilter] = useState('Alle');
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Portfolio & Case Studies | Onze projecten | Noord';
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = 'Bekijk onze webdesign portfolio en case studies. Ontdek hoe wij lokale ondernemers in Noord-Holland helpen groeien met conversiegerichte websites.';
    // Open Graph meta tags
    const ogTags: Record<string, string> = {
      'og:title': 'Portfolio & Case Studies | Onze projecten | Noord',
      'og:description': 'Bekijk onze webdesign portfolio en case studies. Ontdek hoe wij lokale ondernemers helpen groeien.',
      'og:type': 'website',
      'og:url': 'https://noorddigital.nl/portfolio',
      'og:site_name': 'Noord Digital',
      'og:locale': 'nl_NL'
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
  }, []);
  const categories = ['Alle', 'Horeca', 'Zorg', 'Bouw', 'Retail', 'Vastgoed', 'Zakelijk'];
  const caseStudies: CaseStudy[] = [{
    id: 'gouden-leeuw',
    title: 'Meer online reserveringen',
    client: 'Restaurant De Gouden Leeuw',
    sector: 'Horeca',
    category: 'Horeca',
    description: 'Een sfeervolle, snelle website met een geïntegreerd reserveringssysteem. De focus lag op mobiel gebruiksgemak en lokale vindbaarheid.',
    result: '180% meer online reserveringen',
    icon: SmartphoneIcon,
    color: 'from-orange-400 to-red-500'
  }, {
    id: 'fysio-kennemerland',
    title: 'Patiëntenportaal & afspraken',
    client: 'Fysiotherapie Kennemerland',
    sector: 'Zorgpraktijk',
    category: 'Zorg',
    description: 'Een toegankelijke, AVG-proof website met een duidelijk overzicht van specialisaties en een laagdrempelige module voor het inplannen van intakes.',
    result: '95% meer online intakes',
    icon: UsersIcon,
    color: 'from-blue-400 to-indigo-500'
  }, {
    id: 'bouw-vanderberg',
    title: 'Leadgeneratie voor aannemer',
    client: 'Bouwbedrijf Van der Berg',
    sector: 'Bouw & Installatie',
    category: 'Bouw',
    description: 'Een robuuste website die vakmanschap uitstraalt via een uitgebreid projectportfolio, gekoppeld aan een slim offerte-aanvraagformulier.',
    result: '3x meer offerteaanvragen',
    icon: BuildingIcon,
    color: 'from-amber-400 to-orange-500'
  }, {
    id: 'makelaar-gooi',
    title: 'Woningaanbod integratie',
    client: 'Makelaarskantoor Gooi & Meer',
    sector: 'Makelaardij',
    category: 'Vastgoed',
    description: 'Een premium website met automatische koppeling naar het CRM-systeem (Realworks) voor actueel woningaanbod en directe bezichtigingsaanvragen.',
    result: '40% meer bezichtigingen',
    icon: SearchIcon,
    color: 'from-emerald-400 to-teal-500'
  }, {
    id: 'studio-haar',
    title: 'Webshop & salon boekingen',
    client: 'Kapsalon Studio Haar',
    sector: 'Lokale Retail',
    category: 'Retail',
    description: 'Een stijlvolle combinatie van een online afsprakensysteem voor de salon en een WooCommerce webshop voor de verkoop van exclusieve haarproducten.',
    result: '120% meer boekingen',
    icon: ShoppingCartIcon,
    color: 'from-pink-400 to-rose-500'
  }, {
    id: 'horizon-consultancy',
    title: 'Autoriteit & B2B leads',
    client: 'Consultancy Bureau Horizon',
    sector: 'Zakelijke Dienstverlening',
    category: 'Zakelijk',
    description: 'Een strakke, zakelijke website gericht op kennisdeling via whitepapers en blogs, met als doel het genereren van hoogwaardige B2B leads.',
    result: '200% meer B2B leads',
    icon: MonitorIcon,
    color: 'from-slate-600 to-navy-800'
  }];
  const filteredCases = activeFilter === 'Alle' ? caseStudies : caseStudies.filter(c => c.category === activeFilter);
  return <main className="pt-16 lg:pt-[72px]" data-id="element-1634">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 bg-navy-900 overflow-hidden" data-id="element-1635">
        <div className="absolute inset-0 pointer-events-none" data-id="element-1636">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl bg-accent" data-id="element-1637" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-10 blur-3xl bg-accent-light" data-id="element-1638" />
          <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} data-id="element-1639" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1640">
          {/* Breadcrumb */}
          <nav className="flex items-center justify-center gap-2 text-sm mb-8 animate-fade-in-up" data-id="element-1641">
            <a href="#/" className="text-navy-400 hover:text-white transition-colors" data-id="element-1642">
              Home
            </a>
            <span className="text-navy-500" data-id="element-1643">/</span>
            <span className="text-accent-light font-medium" data-id="element-1644">Portfolio</span>
          </nav>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 animate-fade-in-up" data-id="element-1645">
            Onze projecten & case studies
          </h1>
          <p className="text-lg lg:text-xl text-navy-200 max-w-2xl mx-auto mb-10 animate-fade-in-up-delay-1" data-id="element-1646">
            Ontdek hoe wij lokale ondernemers helpen groeien met websites die
            niet alleen mooi zijn, maar vooral meetbaar resultaat opleveren.
          </p>
        </div>
      </section>

      {/* Portfolio Grid Section */}
      <section className="py-24 bg-surface-muted min-h-[600px]" data-id="element-1647">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1648">
          {/* Filters */}
          <div className="flex flex-wrap justify-center gap-3 mb-16" data-id="element-1649">
            {categories.map(category => <button key={category} onClick={() => setActiveFilter(category)} className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${activeFilter === category ? 'bg-navy-900 text-white shadow-md' : 'bg-white text-navy-600 border border-navy-100 hover:border-navy-300 hover:bg-navy-50'}`} data-id="element-1650">
                {category}
              </button>)}
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-1651">
            {filteredCases.map(caseStudy => <div key={caseStudy.id} className="bg-white rounded-2xl shadow-soft hover:shadow-card transition-all duration-300 overflow-hidden group flex flex-col h-full border border-navy-50" data-id="element-1652">
                {/* Image Placeholder Area */}
                <div className={`h-48 w-full bg-gradient-to-br ${caseStudy.color} relative overflow-hidden flex items-center justify-center`} data-id="element-1653">
                  <div className="absolute inset-0 bg-black/10 group-hover:bg-transparent transition-colors duration-300" data-id="element-1654" />
                  <div className="absolute inset-0 opacity-20" style={{
                backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                backgroundSize: '24px 24px'
              }} data-id="element-1655" />

                  <div className="relative z-10 w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/30 transform group-hover:scale-110 transition-transform duration-500" data-id="element-1656">
                    <caseStudy.icon className="w-10 h-10 text-white" data-id="element-1657" />
                  </div>

                  <div className="absolute bottom-4 left-4" data-id="element-1658">
                    <span className="inline-block px-3 py-1 bg-white/90 backdrop-blur-md text-navy-900 text-xs font-bold uppercase tracking-wider rounded-full" data-id="element-1659">
                      {caseStudy.sector}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 flex flex-col flex-grow" data-id="element-1660">
                  <div className="mb-4" data-id="element-1661">
                    <p className="text-sm font-medium text-navy-500 mb-1" data-id="element-1662">
                      {caseStudy.client}
                    </p>
                    <h3 className="text-xl font-bold text-navy-900 group-hover:text-accent transition-colors" data-id="element-1663">
                      {caseStudy.title}
                    </h3>
                  </div>

                  <p className="text-slate-600 text-sm leading-relaxed mb-6 flex-grow" data-id="element-1664">
                    {caseStudy.description}
                  </p>

                  {/* Result Highlight */}
                  <div className="bg-green-50 rounded-xl p-4 mb-6 border border-green-100/50" data-id="element-1665">
                    <div className="flex items-start gap-3" data-id="element-1666">
                      <TrendingUpIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-1667" />
                      <div data-id="element-1668">
                        <p className="text-xs text-green-800 font-medium uppercase tracking-wider mb-0.5" data-id="element-1669">
                          Resultaat
                        </p>
                        <p className="text-green-900 font-bold" data-id="element-1670">
                          {caseStudy.result}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Fake Link */}
                  <div className="mt-auto pt-4 border-t border-navy-50" data-id="element-1671">
                    <span className="inline-flex items-center gap-2 text-sm font-semibold text-navy-900 group-hover:text-accent transition-colors" data-id="element-1672">
                      Bekijk case study
                      <ArrowRightIcon className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" data-id="element-1673" />
                    </span>
                  </div>
                </div>
              </div>)}
          </div>

          {filteredCases.length === 0 && <div className="text-center py-20" data-id="element-1674">
              <p className="text-navy-500 text-lg" data-id="element-1675">
                Geen projecten gevonden in deze categorie.
              </p>
              <button onClick={() => setActiveFilter('Alle')} className="mt-4 text-accent font-medium hover:underline" data-id="element-1676">
                Bekijk alle projecten
              </button>
            </div>}
        </div>
      </section>

      {/* Final CTA */}
      <FinalCtaSection data-id="element-1677" />
    </main>;
}